import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QueueTargetPreview } from "@/components/QueueTargetPreview";

const STATUS_CLS = { CANDIDATE: "bg-amber-100 text-amber-900", GOLD_VERIFIED: "bg-emerald-100 text-emerald-900", REJECTED: "bg-red-100 text-red-900" };

export default function KnowledgeAI() {
  const [data, setData] = useState(null);
  const [family, setFamily] = useState("");
  const [jobIdSel, setJobIdSel] = useState("");
  const [selPreview, setSelPreview] = useState(null);
  const pdfRef = useRef(); const jsonRef = useRef();
  const [ec, setEc] = useState(null);
  const [jobIdCase, setJobIdCase] = useState("");
  const [pkgTitle, setPkgTitle] = useState("");
  const [jobIdExp, setJobIdExp] = useState("");
  const [expPreview, setExpPreview] = useState(null);
  const pkgPdfsRef = useRef(); const pkgJsonRef = useRef();

  const load = () => {
    api.get("/reader/knowledge/references").then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get("/reader/expert-cases").then((r) => setEc(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const importPackage = async () => {
    const files = pkgPdfsRef.current?.files;
    if (!files || files.length === 0) { toast.error("Seleziona almeno un PDF del pacchetto tecnico"); return; }
    const fd = new FormData();
    for (const f of files) fd.append("pdfs", f);
    if (pkgJsonRef.current?.files?.[0]) fd.append("json_file", pkgJsonRef.current.files[0]);
    fd.append("title", pkgTitle);
    try { await api.post("/reader/expert-cases/import", fd); toast.success("Pacchetto importato (IMPORTED) — nessun addestramento automatico"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const caseFromJob = async () => {
    try { const { data: c } = await api.post(`/reader/expert-cases/from-job/${jobIdCase}`); toast.success(`Caso ${c.case_id} creato come CANDIDATE (solo dati HUMAN_APPROVED)`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const expSelector = async () => {
    try { const { data: p } = await api.get(`/reader/expert-cases/selector-preview?job_id=${jobIdExp}`); setExpPreview(p); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doImport = async () => {
    const pdf = pdfRef.current?.files?.[0]; const js = jsonRef.current?.files?.[0];
    if (!pdf || !js) { toast.error("Servono PDF + JSON"); return; }
    const fd = new FormData();
    fd.append("pdf", pdf); fd.append("json_file", js); fd.append("drawing_family", family);
    try { await api.post("/reader/knowledge/import", fd); toast.success("Coppia importata come CANDIDATE"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const classify = async (refId, status) => {
    const note = status === "GOLD_VERIFIED" ? window.prompt("Verifica umana: nota di promozione GOLD (obbligatoria)") : window.prompt("Nota (opzionale)") || "";
    if (status === "GOLD_VERIFIED" && !note) { toast.error("Promozione GOLD annullata: serve la nota di verifica umana"); return; }
    try {
      await api.post(`/reader/knowledge/${refId}/classify`, { status, note, human_verified: status === "GOLD_VERIFIED" });
      toast.success(`${refId} → ${status} (append-only)`); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const preview = async () => {
    try { const { data: d } = await api.get(`/reader/knowledge/selector-preview?job_id=${jobIdSel}`); setSelPreview(d); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!data) return <div className="p-8 text-sm">Caricamento…</div>;
  return (
    <div className="p-5 space-y-4" data-testid="knowledge-page">
      <div>
        <h1 className="text-lg font-display font-bold">Conoscenza esperta</h1>
        <p className="text-xs text-muted-foreground">Il tecnico esperto insegna a QuoteOS a distinguere pezzo singolo, assieme e complessivo; a collegare distinta, pallinatura e particolari; a riconoscere sviluppo e sagoma piegata; a scomporre senza inventare componenti. Ciclo: IMPORTED → CANDIDATE → EXPERT_LABELING → READY_FOR_VERIFICATION → GOLD_VERIFIED.</p>
      </div>
      <div className="border border-amber-400 bg-amber-50 p-2 text-xs font-bold text-amber-900" data-testid="no-auto-training-banner">
        Caricare file non addestra automaticamente l'AI. Il caso diventa utilizzabile solamente dopo verifica tecnica e promozione GOLD.
      </div>
      <div className="border border-red-300 bg-red-50 p-2 text-xs font-bold text-red-800" data-testid="ref-aug-banner">
        REFERENCE_AUGMENTATION = {data.reference_augmentation} — in questa fase NESSUN esempio GOLD viene inviato a OpenAI.
      </div>

      <QueueTargetPreview />

      <div className="border-2 border-border bg-white text-xs" data-testid="expert-cases-list">
        <div className="px-3 py-1.5 bg-muted font-bold">Casi di insegnamento esperto ({ec?.cases?.length ?? 0}) — GOLD: {ec?.gold_count ?? 0} · promozione MAI automatica</div>
        <table className="w-full">
          <thead><tr className="text-left border-b border-border">
            {["Caso", "Titolo", "Stato", "Origine", "Tipo pacchetto", "Scomposizione", "Doc.", "Codice", "Azioni"].map((h) => <th key={h} className="px-2 py-1">{h}</th>)}
          </tr></thead>
          <tbody>{(ec?.cases || []).map((c) => (
            <tr key={c.case_id} className="border-b border-border/50" data-testid={`case-row-${c.case_id}`}>
              <td className="px-2 py-1 font-bold">{c.case_id}</td>
              <td className="px-2 py-1">{c.title}</td>
              <td className="px-2 py-1"><span className={`px-1 text-[9px] font-bold ${c.status === "GOLD_VERIFIED" ? "bg-emerald-100 text-emerald-900" : c.status === "REJECTED" ? "bg-red-100 text-red-900" : "bg-amber-100 text-amber-900"}`}>{c.status}</span></td>
              <td className="px-2 py-1">{c.origin}</td>
              <td className="px-2 py-1">{c.classification?.document_type}</td>
              <td className="px-2 py-1">{c.classification?.decomposition_status}</td>
              <td className="px-2 py-1">{(c.documents || []).length}</td>
              <td className="px-2 py-1 font-mono">{c.drawing_code || "—"}</td>
              <td className="px-2 py-1"><Link to={`/knowledge/case/${c.case_id}`} className="underline font-bold" data-testid={`open-case-${c.case_id}`}>Apri editor</Link></td>
            </tr>))}
            {(!ec || ec.cases.length === 0) && <tr><td colSpan="9" className="px-2 py-3 text-muted-foreground">Nessun caso: importa un pacchetto tecnico o crea un caso da una RFQ revisionata.</td></tr>}
          </tbody>
        </table>
        <div className="px-3 py-2 border-t border-border space-y-2">
          <div className="flex flex-wrap items-center gap-2" data-testid="case-from-job-box">
            <span className="font-bold">B. Crea caso da RFQ revisionata (solo dati HUMAN_APPROVED):</span>
            <Input placeholder="job_id lettura approvata" className="h-7 w-64 text-xs" value={jobIdCase} onChange={(e) => setJobIdCase(e.target.value)} data-testid="case-from-job-id" />
            <Button size="sm" onClick={caseFromJob} data-testid="case-from-job-submit">Crea caso CANDIDATE</Button>
            <span className="text-muted-foreground">quantità commessa, prezzi e preventivo ESCLUSI</span>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-testid="case-import-box">
            <span className="font-bold">A/C. Importa pacchetto tecnico (uno o più PDF + JSON opzionale):</span>
            <label>PDF <input type="file" accept=".pdf" multiple ref={pkgPdfsRef} data-testid="package-pdfs" /></label>
            <label>JSON <input type="file" accept=".json" ref={pkgJsonRef} data-testid="package-json" /></label>
            <Input placeholder="titolo pacchetto" className="h-7 w-48 text-xs" value={pkgTitle} onChange={(e) => setPkgTitle(e.target.value)} data-testid="package-title" />
            <Button size="sm" onClick={importPackage} data-testid="package-import-submit">Importa pacchetto (IMPORTED)</Button>
          </div>
        </div>
      </div>

      <div className="border border-border bg-white p-3 text-xs space-y-2" data-testid="expert-selector-preview">
        <div className="font-bold">Selettore futuro di riferimenti (ANTEPRIMA non attiva — max 2 casi GOLD, esclusi stesso SHA e stesso codice/revisione, conoscenza privata mai condivisa)</div>
        <div className="flex gap-2 items-center">
          <Input placeholder="job_id nuovo disegno" className="h-7 w-64 text-xs" value={jobIdExp} onChange={(e) => setJobIdExp(e.target.value)} data-testid="expert-selector-jobid" />
          <Button size="sm" variant="outline" onClick={expSelector} data-testid="expert-selector-run">Anteprima selezione</Button>
        </div>
        {expPreview && (
          <div className="border border-border p-2 bg-muted/40 space-y-1" data-testid="expert-selector-result">
            <div>Target: sha {String(expPreview.target.pdf_sha256 || "").slice(0, 12)}… · codice {expPreview.target.drawing_code} rev.{expPreview.target.revision} · lavorazioni: {expPreview.target.process_tags.join(", ") || "—"}</div>
            <div>Selezionati (max 2): {expPreview.selected_gold_cases.length ? expPreview.selected_gold_cases.map((s) => `${s.case_id} — ${s.motivazione}`).join(" · ") : "NESSUNO (nessun caso GOLD pertinente)"}</div>
            {expPreview.excluded.length > 0 && <div className="text-red-800">Esclusi: {expPreview.excluded.map((x) => `${x.case_id}: ${x.reason}`).join(" · ")}</div>}
            <div className="text-muted-foreground">{expPreview.note}</div>
          </div>
        )}
      </div>

      <div className="border border-border bg-white p-3 text-xs space-y-2" data-testid="knowledge-import">
        <div className="font-bold">Importa coppia PDF + JSON (dalla vecchia chat)</div>
        <div className="flex flex-wrap items-center gap-2">
          <label>PDF <input type="file" accept=".pdf" ref={pdfRef} data-testid="import-pdf" /></label>
          <label>JSON <input type="file" accept=".json" ref={jsonRef} data-testid="import-json" /></label>
          <Input placeholder="drawing_family (opzionale)" className="h-7 w-56 text-xs" value={family} onChange={(e) => setFamily(e.target.value)} data-testid="import-family" />
          <Button size="sm" onClick={doImport} data-testid="import-submit">Importa come CANDIDATE</Button>
        </div>
        <p className="text-muted-foreground">Il JSON viene normalizzato nel contratto corrente senza alterarne il significato (originale conservato).</p>
      </div>

      <div className="border border-border bg-white text-xs" data-testid="knowledge-refs">
        <div className="px-3 py-1.5 bg-muted font-bold">Riferimenti libreria ({data.entries.length})</div>
        <table className="w-full">
          <thead><tr className="text-left border-b border-border">
            {["Ref", "Stato", "Origine", "Famiglia", "SHA PDF", "Schema", "Revisore", "Azioni"].map((h) => <th key={h} className="px-2 py-1">{h}</th>)}
          </tr></thead>
          <tbody>{data.entries.map((e) => (
            <tr key={e.ref_id} className="border-b border-border/50" data-testid={`ref-${e.ref_id}`}>
              <td className="px-2 py-1 font-bold">{e.ref_id}</td>
              <td className="px-2 py-1"><span className={`px-1 text-[9px] font-bold ${STATUS_CLS[e.status] || "bg-secondary"}`}>{e.status}</span></td>
              <td className="px-2 py-1">{e.origin}</td>
              <td className="px-2 py-1">{e.drawing_family || "—"}</td>
              <td className="px-2 py-1 font-mono">{(e.pdf_sha256 || "").slice(0, 10)}…</td>
              <td className="px-2 py-1">{e.schema || "—"}</td>
              <td className="px-2 py-1">{e.reviewer || "—"}</td>
              <td className="px-2 py-1 flex gap-1">
                {e.status !== "GOLD_VERIFIED" && <button className="border border-border px-1 hover:bg-emerald-50" data-testid={`gold-${e.ref_id}`} onClick={() => classify(e.ref_id, "GOLD_VERIFIED")}>Promuovi GOLD</button>}
                {e.status !== "REJECTED" && <button className="border border-border px-1 hover:bg-red-50" data-testid={`reject-${e.ref_id}`} onClick={() => classify(e.ref_id, "REJECTED")}>Rifiuta</button>}
              </td>
            </tr>))}</tbody>
        </table>
      </div>

      <div className="border border-border bg-white text-xs" data-testid="gold-candidates">
        <div className="px-3 py-1.5 bg-muted font-bold">GOLD_CANDIDATE da letture live approvate ({data.gold_candidates.length}) — promozione MAI automatica</div>
        {data.gold_candidates.map((c) => (
          <div key={c.id} className="px-3 py-1.5 border-b border-border/50">
            job {c.job_id?.slice(0, 8)}… · raw {c.raw_hash?.slice(0, 10)}… · requisiti: {(c.promotion_requirements || []).join("; ")}
          </div>))}
      </div>

      <div className="border border-border bg-white p-3 text-xs space-y-2" data-testid="selector-preview">
        <div className="font-bold">Selettore esempi (anteprima futura — max 2, solo GOLD_VERIFIED, target escluso per SHA)</div>
        <div className="flex gap-2 items-center">
          <Input placeholder="job_id lettura" className="h-7 w-64 text-xs" value={jobIdSel} onChange={(e) => setJobIdSel(e.target.value)} data-testid="selector-jobid" />
          <Button size="sm" variant="outline" onClick={preview} data-testid="selector-run">Anteprima selezione</Button>
        </div>
        {selPreview && (
          <div className="border border-border p-2 bg-muted/40">
            <div>Tag processi target: {selPreview.job_process_tags.join(", ") || "—"}</div>
            <div>Selezionati: {selPreview.selected_examples.length ? selPreview.selected_examples.map((s) => `${s.ref_id} (${s.match})`).join(", ") : "NESSUNO (nessun esempio realmente pertinente)"}</div>
            <div className="text-muted-foreground">{selPreview.note}</div>
          </div>
        )}
      </div>

      <div className="border border-border bg-white text-xs" data-testid="promotions-log">
        <div className="px-3 py-1.5 bg-muted font-bold">Registro promozioni (append-only, {data.promotions_log.length})</div>
        {data.promotions_log.map((p) => (
          <div key={p.id} className="px-3 py-1 border-b border-border/50">
            {p.at?.slice(0, 19)} · {p.ref_id} · {p.action}: {p.from_status || "∅"} → {p.to_status} · {p.reviewer_name}{p.note ? ` · "${p.note}"` : ""}
          </div>))}
      </div>
    </div>
  );
}
